import type { ValuedInformation } from 'dashboard409a/services/shared-types';

export const DEFAULT_VISIBLE_FIELDS = [
  'valuationDate',
  'valuationFee',
  'valuationNetValue',
  'split',
  'bookingDate',
] as const;

export type FinancialFieldKey =
  | 'valuationDate'
  | 'valuationFee'
  | 'valuationNetValue'
  | 'bookingDate'
  | 'renewalDate'
  | 'renewalRate'
  | 'split'
  | 'payment1'
  | 'payment2'
  | 'payment3'
  | 'refund'
  | 'refundDate'
  | 'adjustment'
  | 'adjustmentDate'
  | 'badDebt'
  | 'badDebtDate'
  | 'due';

export interface FinancialFieldConfig {
  key: FinancialFieldKey;
  label: string;
  isCurrency: boolean;
  getValue: (valuedInformation?: ValuedInformation) => string | number | undefined;
  getDateValue?: (valuedInformation?: ValuedInformation) => string | undefined;
}

export const getFinancialFields = (
  valuedInformation?: ValuedInformation
): FinancialFieldConfig[] => [
  {
    key: 'valuationDate',
    label: 'Valuation Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.valuationDate,
  },
  {
    key: 'valuationFee',
    label: 'Valuation Fee:',
    isCurrency: true,
    getValue: () => valuedInformation?.valuationFee,
  },
  {
    key: 'valuationNetValue',
    label: 'Net Value:',
    isCurrency: true,
    getValue: () => {
      if (valuedInformation?.valuationNetValue && valuedInformation.valuationNetValue !== '-') {
        return Number(valuedInformation.valuationNetValue);
      }
      return valuedInformation?.valuationNetValue;
    },
  },
  {
    key: 'split',
    label: 'Split:',
    isCurrency: true,
    getValue: () => valuedInformation?.split,
  },
  {
    key: 'bookingDate',
    label: 'Booking Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.bookingDate,
  },
  {
    key: 'renewalDate',
    label: 'Renewal Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.renewalDate,
  },
  {
    key: 'due',
    label: 'Due:',
    isCurrency: true,
    getValue: () => valuedInformation?.due,
  },
  {
    key: 'renewalRate',
    label: 'Renewal Rate:',
    isCurrency: true,
    getValue: () => valuedInformation?.renewalRate,
  },
  {
    key: 'payment1',
    label: 'Payment 1:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment1,
    getDateValue: () => valuedInformation?.payment1Date,
  },
  {
    key: 'payment2',
    label: 'Payment 2:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment2,
    getDateValue: () => valuedInformation?.payment2Date,
  },
  {
    key: 'payment3',
    label: 'Payment 3:',
    isCurrency: true,
    getValue: () => valuedInformation?.payment3,
    getDateValue: () => valuedInformation?.payment3Date,
  },
  {
    key: 'refund',
    label: 'Refund:',
    isCurrency: true,
    getValue: () => valuedInformation?.refund,
  },
  {
    key: 'refundDate',
    label: 'Refund Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.refundDate,
  },
  {
    key: 'adjustment',
    label: 'Adjustment:',
    isCurrency: true,
    getValue: () => valuedInformation?.adjustment,
  },
  {
    key: 'adjustmentDate',
    label: 'Adjustment Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.adjustmentDate,
  },
  {
    key: 'badDebt',
    label: 'Bad Debt:',
    isCurrency: true,
    getValue: () => valuedInformation?.badDebt,
  },
  {
    key: 'badDebtDate',
    label: 'Bad Debt Date:',
    isCurrency: false,
    getValue: () => valuedInformation?.badDebtDate,
  },
];
